import React, { useState } from 'react';
import { X, Plus, Edit2, Trash2, Save, QrCode } from 'lucide-react';
import SurveySetup from './Survey/SurveySetup';

interface SettingsPanelProps {
  presetTexts: string[];
  keywords: string[];
  onSave: (presets: string[], keywords: string[]) => void;
  onClose: () => void;
  storeName: string;
  user: { email?: string };
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  presetTexts: initialPresets,
  keywords: initialKeywords,
  onSave,
  onClose,
  storeName,
  user
}) => {
  const [presetTexts, setPresetTexts] = useState<string[]>([...initialPresets]);
  const [keywords, setKeywords] = useState<string[]>([...initialKeywords]);
  const [newPreset, setNewPreset] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [activeTab, setActiveTab] = useState<'reply' | 'survey'>('reply');
  const userEmail = user?.email || "";

  const addPreset = () => {
    if (newPreset.trim()) {
      setPresetTexts([...presetTexts, newPreset.trim()]);
      setNewPreset('');
    }
  };

  const editPreset = (index: number, newText: string) => {
    const updated = [...presetTexts];
    updated[index] = newText;
    setPresetTexts(updated);
  };

  const deletePreset = (index: number) => {
    setPresetTexts(presetTexts.filter((_, i) => i !== index));
  };

  const addKeyword = () => {
    if (newKeyword.trim()) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const editKeyword = (index: number, newText: string) => {
    const updated = [...keywords];
    updated[index] = newText;
    setKeywords(updated);
  };

  const deleteKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(presetTexts, keywords);
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-6 relative">
      <div className="absolute top-3 right-3">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 focus:outline-none"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">設定</h2>

        {/* タブナビゲーション */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('reply')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'reply'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                返信設定
              </button>
              <button
                onClick={() => setActiveTab('survey')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'survey'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <QrCode className="h-4 w-4 inline mr-1" />
                アンケート・QR
              </button>
            </nav>
          </div>
        </div>

        {/* タブコンテンツ */}
        {activeTab === 'reply' && (
          <>
            {/* Presets Section */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                <span className="bg-[#E6F0FF] text-[#007AFF] px-2 py-1 rounded-md mr-2 text-xs">
                  定型文設定
                </span>
              </h3>

              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto p-2">
                {presetTexts.map((text, index) => (
                  <div key={index} className="flex items-start group p-2 hover:bg-gray-50 rounded-md">
                    <div className="flex-grow mr-2">
                      <p className="text-sm text-gray-700">{text}</p>
                    </div>
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => {
                          const newText = prompt("定型文を編集してください", text);
                          if (newText !== null) {
                            editPreset(index, newText.trim());
                          }
                        }}
                        className="text-[#007AFF] hover:text-[#007AFF] p-1"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deletePreset(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {presetTexts.length === 0 && (
                  <p className="text-sm text-gray-500 italic text-center py-2">
                    定型文がありません
                  </p>
                )}
              </div>

              <div className="flex">
                <textarea
                  value={newPreset}
                  onChange={(e) => setNewPreset(e.target.value)}
                  placeholder="定型文を入力"
                  className="flex-grow shadow-sm focus:ring-[#007AFF] focus:border-[#007AFF] block w-full sm:text-sm border-gray-300 rounded-md"
                  rows={3}
                />
                <button
                  onClick={addPreset}
                  className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#007AFF] hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] self-end"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  追加
                </button>
              </div>
            </div>

            {/* Keywords Section */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md mr-2 text-xs">
                  キーワード設定
                </span>
              </h3>

              <div className="flex flex-wrap gap-2 mb-4 max-h-40 overflow-y-auto p-2">
                {keywords.map((keyword, index) => (
                  <div key={index} className="bg-gray-100 rounded-full px-3 py-1 text-sm text-gray-700 flex items-center group">
                    <span>{keyword}</span>
                    <div className="ml-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => {
                          const newText = prompt("キーワードを編集してください", keyword);
                          if (newText !== null) {
                            editKeyword(index, newText.trim());
                          }
                        }}
                        className="text-[#007AFF] hover:text-[#007AFF]"
                      >
                        <Edit2 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => deleteKeyword(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {keywords.length === 0 && (
                  <p className="text-sm text-gray-500 italic text-center py-2 w-full">
                    キーワードがありません
                  </p>
                )}
              </div>

              <div className="flex">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="キーワードを入力"
                  className="flex-grow shadow-sm focus:ring-[#007AFF] focus:border-[#007AFF] block w-full sm:text-sm border-gray-300 rounded-md"
                />
                <button
                  onClick={addKeyword}
                  className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  追加
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#007AFF] hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
              >
                <Save className="mr-2 h-4 w-4" />
                設定を保存
              </button>
            </div>
          </>
        )}

        {/* アンケート・QRタブ */}
        {activeTab === 'survey' && (
          <SurveySetup shopName={storeName} userEmail={userEmail} />
        )}
      </div>
    </div>
  );
};

export default SettingsPanel;
